<template>
  <div>


    <b-tabs>
      <!-- =========================================================
           WEEKLY PATTERN
      ========================================================== -->
      <b-tab active>
        <template #title>
          <feather-icon icon="RepeatIcon" size="16" class="mr-0 mr-sm-50" />
          <span class="d-none d-sm-inline">Weekly Pattern</span>
        </template>

        <div class="mt-2">
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
            <div>
              
            </div>

            <b-button
              variant="primary"
              size="sm"
              @click="openPatternModal()"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              Add Pattern
            </b-button>
          </div>

          <div
            v-if="loadingPattern"
            class="text-center py-3"
          >
            <b-spinner small />
            <span class="ml-50">Loading...</span>
          </div>

          <template v-else>
            <b-table
              responsive
              striped
              hover
              show-empty
              :items="patternRows"
              :fields="patternFields"
              empty-text="No weekly roster pattern"
            >
              <template #cell(weekday)="data">
                <strong>{{ weekdayName(data.item.weekday) }}</strong>
              </template>

              <template #cell(time)="data">
                <span class="font-weight-bold">
                  {{ shortTime(data.item.start_time) }}
                  -
                  {{ shortTime(data.item.end_time) }}
                </span>
              </template>

              <template #cell(center)="data">
                {{ centerName(data.item.center_id) }}
              </template>

              <template #cell(position)="data">
                <span v-if="data.item.position">
                  {{ data.item.position }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(period)="data">
                <div>{{ data.item.effective_date }}</div>
                <small class="text-muted">
                  to {{ data.item.expire_date || 'No Expiry' }}
                </small>
              </template>

              <template #cell(status)="data">
                <b-badge :variant="patternStatusVariant(data.item.status)">
                  {{ data.item.status }}
                </b-badge>
              </template>

              <template #cell(actions)="data">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-50"
                  @click="openPatternModal(data.item)"
                >
                  <feather-icon icon="Edit2Icon" />
                </b-button>

                <b-button
                  size="sm"
                  variant="outline-danger"
                  @click="confirmDeletePattern(data.item)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </template>
            </b-table>
          </template>
        </div>
      </b-tab>

      <!-- =========================================================
           DAILY ROSTER
      ========================================================== -->
      <b-tab>
        <template #title>
          <feather-icon icon="CalendarIcon" size="16" class="mr-0 mr-sm-50" />
          <span class="d-none d-sm-inline">Daily Roster</span>
        </template>

        <div class="mt-1">
          <b-row class="align-items-end">
            <b-col cols="12" md="3">
              <b-form-group>
                <b-form-input
                  v-model="rosterFilter.from"
                  type="date"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="3">
              <b-form-group >
                <b-form-input
                  v-model="rosterFilter.to"
                  type="date"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
                <b-button
                  variant="outline-primary"
                  class="mr-1"
                  @click="getRosterData"
                >
                  <feather-icon icon="SearchIcon" class="mr-50" />
                  Search
                </b-button>

                <b-button
                  variant="primary"
                  @click="openRosterModal()"
                >
                  <feather-icon icon="PlusIcon" class="mr-50" />
                  Add Daily Roster
                </b-button>
            </b-col>
          </b-row>

          <div
            v-if="loadingRoster"
            class="text-center py-3"
          >
            <b-spinner small />
            <span class="ml-50">Loading...</span>
          </div>

          <template v-else>
            <b-table
              responsive
              striped
              hover
              show-empty
              class="mt-1"
              :items="rosterRows"
              :fields="rosterFields"
              empty-text="No daily roster"
            >
              <template #cell(roster_date)="data">
                <div class="font-weight-bold">
                  {{ data.item.roster_date }}
                </div>
                <small class="text-muted">
                  {{ dateWeekday(data.item.roster_date) }}
                </small>
              </template>

              <template #cell(time)="data">
                <span>
                  {{ datetimeTime(data.item.start) }}
                  -
                  {{ datetimeTime(data.item.end) }}
                </span>
              </template>

              <template #cell(center)="data">
                {{ centerName(data.item.center_id) }}
              </template>

              <template #cell(position)="data">
                <span v-if="data.item.position">
                  {{ data.item.position }}
                </span>
                <span v-else-if="data.item.position_name">
                  {{ data.item.position_name }}
                </span>
                <span v-else-if="data.item.position_id">
                  #{{ data.item.position_id }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(source)="data">
                <b-badge :variant="sourceVariant(data.item.roster_source)">
                  {{ data.item.roster_source }}
                </b-badge>
              </template>

              <template #cell(leave)="data">
                <span v-if="data.item.is_leave === 'Y'">
                  <b-badge variant="warning">Leave</b-badge>
                  <small
                    v-if="data.item.leave_type"
                    class="ml-50"
                  >
                    {{ data.item.leave_type }}
                  </small>
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(status)="data">
                <b-badge :variant="rosterStatusVariant(data.item.status)">
                  {{ data.item.status }}
                </b-badge>
              </template>

              <template #cell(actions)="data">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-50"
                  @click="openRosterModal(data.item)"
                >
                  <feather-icon icon="Edit2Icon" />
                </b-button>

                <b-button
                  size="sm"
                  variant="outline-danger"
                  @click="confirmDeleteRoster(data.item)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </template>
            </b-table>
          </template>
        </div>
      </b-tab>
    </b-tabs>

    <!-- =========================================================
         PATTERN MODAL
    ========================================================== -->
    <b-modal
      id="pattern-modal"
      v-model="patternModal"
      :title="patternForm.id ? 'Edit Weekly Pattern' : 'Add Weekly Pattern'"
      hide-footer
      no-close-on-backdrop
    >
      <b-form @submit.prevent="savePattern">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Weekday" label-for="pattern-weekday">
              <v-select
                id="pattern-weekday"
                v-model="patternForm.weekday"
                :options="weekdayOptions"
                :reduce="option => option.value"
                :clearable="false"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Center" label-for="pattern-center">
              <v-select
                id="pattern-center"
                v-model="patternForm.center_id"
                :options="centerOptions"
                :reduce="option => option.value"
                :clearable="false"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Start Time" label-for="pattern-start">
              <b-form-input
                id="pattern-start"
                v-model="patternForm.start_time"
                type="time"
                required
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="End Time" label-for="pattern-end">
              <b-form-input
                id="pattern-end"
                v-model="patternForm.end_time"
                type="time"
                required
              />
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Position" label-for="pattern-position">
              <b-form-input
                id="pattern-position"
                v-model.trim="patternForm.position"
                placeholder="e.g. Teacher / CS / Reception"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Effective Date" label-for="pattern-effective">
              <b-form-input
                id="pattern-effective"
                v-model="patternForm.effective_date"
                type="date"
                required
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Expire Date" label-for="pattern-expire">
              <b-form-input
                id="pattern-expire"
                v-model="patternForm.expire_date"
                type="date"
              />
              <small class="text-muted">
                Leave blank = no expiry
              </small>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Status" label-for="pattern-status">
              <v-select
                id="pattern-status"
                v-model="patternForm.status"
                :options="patternStatusOptions"
                :reduce="option => option.value"
                :clearable="false"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Remarks" label-for="pattern-remarks">
              <b-form-input
                id="pattern-remarks"
                v-model.trim="patternForm.remarks"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="patternModal = false"
          >
            Cancel
          </b-button>

          <b-button
            type="submit"
            variant="primary"
            :disabled="savingPattern"
          >
            <b-spinner v-if="savingPattern" small class="mr-50" />
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- =========================================================
         DAILY ROSTER MODAL
    ========================================================== -->
    <b-modal
      id="roster-modal"
      v-model="rosterModal"
      :title="rosterForm.id ? 'Edit Daily Roster' : 'Add Daily Roster'"
      hide-footer
      no-close-on-backdrop
    >
      <b-form @submit.prevent="saveRoster">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Roster Date" label-for="roster-date">
              <b-form-input
                id="roster-date"
                v-model="rosterForm.roster_date"
                type="date"
                required
                @change="syncRosterDateTime"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Center" label-for="roster-center">
              <v-select
                id="roster-center"
                v-model="rosterForm.center_id"
                :options="centerOptions"
                :reduce="option => option.value"
                :clearable="false"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Start Time" label-for="roster-start">
              <b-form-input
                id="roster-start"
                v-model="rosterForm.start_time"
                type="time"
                required
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="End Time" label-for="roster-end">
              <b-form-input
                id="roster-end"
                v-model="rosterForm.end_time"
                type="time"
                required
              />
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Position" label-for="roster-position">
              <b-form-input
                id="roster-position"
                v-model.trim="rosterForm.position"
                placeholder="e.g. Teacher / CS / Reception"
              />
              <small class="text-muted">
                <!-- 目前你的 tbl_roster 使用 position_id；如果 API 使用 position_id，
                請在 backend 將這欄按你的 Position 設計轉換。 -->
              </small>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Source" label-for="roster-source">
              <v-select
                id="roster-source"
                v-model="rosterForm.roster_source"
                :options="rosterSourceOptions"
                :reduce="option => option.value"
                :clearable="false"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Status" label-for="roster-status">
              <v-select
                id="roster-status"
                v-model="rosterForm.status"
                :options="rosterStatusOptions"
                :reduce="option => option.value"
                :clearable="false"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-checkbox
              v-model="rosterForm.is_leave"
              value="Y"
              unchecked-value="N"
              class="mb-1"
            >
              Leave
            </b-form-checkbox>
          </b-col>

          <b-col
            v-if="rosterForm.is_leave === 'Y'"
            cols="12"
          >
            <b-form-group label="Leave Type" label-for="leave-type">
              <b-form-input
                id="leave-type"
                v-model.trim="rosterForm.leave_type"
                placeholder="e.g. ANNUAL / SICK / NO_PAY"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Remarks" label-for="roster-remarks">
              <b-form-input
                id="roster-remarks"
                v-model.trim="rosterForm.remarks"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="rosterModal = false"
          >
            Cancel
          </b-button>

          <b-button
            type="submit"
            variant="primary"
            :disabled="savingRoster"
          >
            <b-spinner v-if="savingRoster" small class="mr-50" />
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCol,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BModal,
  BRow,
  BSpinner,
  BTab,
  BTable,
  BTabs,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'TabStaffRoster',

  components: {
    BAlert,
    BBadge,
    BButton,
    BCol,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BModal,
    BRow,
    BSpinner,
    BTab,
    BTable,
    BTabs,
    vSelect,
  },

  props: {
    userData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      centerOptions: [],

      loadingPattern: false,
      savingPattern: false,
      loadingRoster: false,
      savingRoster: false,

      patternRows: [],
      rosterRows: [],

      patternModal: false,
      rosterModal: false,

      patternForm: this.emptyPatternForm(),
      rosterForm: this.emptyRosterForm(),

      rosterFilter: {
        from: this.firstDayOfMonth(),
        to: this.lastDayOfMonth(),
      },

      weekdayOptions: [
        { label: 'Monday', value: 1 },
        { label: 'Tuesday', value: 2 },
        { label: 'Wednesday', value: 3 },
        { label: 'Thursday', value: 4 },
        { label: 'Friday', value: 5 },
        { label: 'Saturday', value: 6 },
        { label: 'Sunday', value: 7 },
      ],

      patternStatusOptions: [
        { label: 'ACTIVE', value: 'ACTIVE' },
        { label: 'DISABLED', value: 'DISABLED' },
        { label: 'DELETED', value: 'DELETED' },
      ],

      rosterSourceOptions: [
        { label: 'Manual', value: 'MANUAL' },
        { label: 'Override', value: 'OVERRIDE' },
      ],

      rosterStatusOptions: [
        { label: 'ACTIVE', value: 'ACTIVE' },
        { label: 'CANCELLED', value: 'CANCELLED' },
        { label: 'DELETED', value: 'DELETED' },
      ],

      patternFields: [
        { key: 'weekday', label: 'Day' },
        { key: 'time', label: 'Time' },
        { key: 'center', label: 'Center' },
        { key: 'position', label: 'Position' },
        { key: 'period', label: 'Effective Period' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: '', class: 'text-right' },
      ],

      rosterFields: [
        { key: 'roster_date', label: 'Date' },
        { key: 'time', label: 'Time' },
        { key: 'center', label: 'Center' },
        { key: 'position', label: 'Position' },
        { key: 'source', label: 'Source' },
        { key: 'leave', label: 'Leave' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: '', class: 'text-right' },
      ],
    }
  },

  computed: {
    userId() {
      return this.userData.id || this.userData.user_id || null
    },

    staffName() {
      return this.userData.display_name || `User #${this.userId || ''}`
    },
  },

  watch: {
    userId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.refreshAll()
        }
      },
    },
  },

  created() {
    this.getCenterData()
  },

  methods: {
    /* =========================================================
       API PATHS
       Change ONLY this section if your backend route is different.
    ========================================================== */
    apiPatternList() {
      return `${process.env.VUE_APP_API_BASE}/staff/${this.userId}/roster-pattern/`
    },

    apiPatternItem(id) {
      return `${process.env.VUE_APP_API_BASE}/staff/${this.userId}/roster-pattern/${id}/`
    },

    apiRosterList() {
      return `${process.env.VUE_APP_API_BASE}/staff/${this.userId}/roster/`
    },

    apiRosterItem(id) {
      return `${process.env.VUE_APP_API_BASE}/staff/${this.userId}/roster/${id}/`
    },

    /* =========================================================
       INITIAL / REFRESH
    ========================================================== */
    refreshAll() {
      if (!this.userId) return

      this.getPatternData()
      this.getRosterData()
    },

    getCenterData() {
      this.$http
        .get(`${process.env.VUE_APP_API_BASE}/centers/`)
        .then(res => {
          const centers = res.data.centers || []

          this.centerOptions = centers.map(center => ({
            label: center.label || center.text || center.name || center.zh_name || center.en_name || `Center ${center.value || center.id}`,
            value: Number(center.value || center.id),
          }))
        })
        .catch(() => {
          this.showError('Cannot load center list.')
        })
    },

    /* =========================================================
       WEEKLY PATTERN
    ========================================================== */
    getPatternData() {
      if (!this.userId) return

      this.loadingPattern = true

      this.$http
        .get(this.apiPatternList())
        .then(res => {
          this.patternRows = res.data.patterns
            || res.data.roster_pattern
            || res.data.records
            || res.data.data
            || []
        })
        .catch(error => {
          this.patternRows = []
          this.showApiError(error, 'Cannot load weekly roster pattern.')
        })
        .finally(() => {
          this.loadingPattern = false
        })
    },

    openPatternModal(item = null) {
      if (item) {
        this.patternForm = {
          id: item.id,
          user_id: this.userId,
          center_id: Number(item.center_id),
          position: item.position || '',
          weekday: Number(item.weekday),
          start_time: this.shortTime(item.start_time),
          end_time: this.shortTime(item.end_time),
          effective_date: item.effective_date,
          expire_date: item.expire_date || '',
          status: item.status || 'ACTIVE',
          remarks: item.remarks || '',
        }
      } else {
        this.patternForm = this.emptyPatternForm()
      }

      this.patternModal = true
    },

    savePattern() {
      const validation = this.validatePattern()

      if (validation !== true) {
        this.showError(validation)
        return
      }

      this.savingPattern = true

      const payload = {
        user_id: this.userId,
        center_id: Number(this.patternForm.center_id),
        position: this.patternForm.position || null,
        weekday: Number(this.patternForm.weekday),
        start_time: this.normalizeTime(this.patternForm.start_time),
        end_time: this.normalizeTime(this.patternForm.end_time),
        effective_date: this.patternForm.effective_date,
        expire_date: this.patternForm.expire_date || null,
        status: this.patternForm.status,
        remarks: this.patternForm.remarks || null,
      }

      const request = this.patternForm.id
        ? this.$http.post(`${this.apiPatternItem(this.patternForm.id)}edit/`, payload)
        : this.$http.post(this.apiPatternList(), payload)

      request
        .then(res => {
          if (res.data.result === false || res.data.success === false) {
            this.showError(res.data.message || 'Save failed.')
            return
          }

          this.patternModal = false
          this.showSuccess(res.data.message || 'Weekly roster saved.')
          this.getPatternData()
        })
        .catch(error => {
          this.showApiError(error, 'Cannot save weekly roster.')
        })
        .finally(() => {
          this.savingPattern = false
        })
    },

    confirmDeletePattern(item) {
      this.$swal({
        title: 'Delete weekly pattern?',
        text: `${this.weekdayName(item.weekday)} ${this.shortTime(item.start_time)} - ${this.shortTime(item.end_time)}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deletePattern(item)
        }
      })
    },

    deletePattern(item) {
      this.$http
        .post(`${this.apiPatternItem(item.id)}delete/`, {})
        .then(res => {
          if (res.data.result === false || res.data.success === false) {
            this.showError(res.data.message || 'Delete failed.')
            return
          }

          this.showSuccess(res.data.message || 'Weekly pattern deleted.')
          this.getPatternData()
        })
        .catch(error => {
          this.showApiError(error, 'Cannot delete weekly pattern.')
        })
    },

    validatePattern() {
      if (!this.patternForm.weekday) return 'Please select weekday.'
      if (!this.patternForm.center_id) return 'Please select center.'
      if (!this.patternForm.start_time) return 'Please enter start time.'
      if (!this.patternForm.end_time) return 'Please enter end time.'
      if (!this.patternForm.effective_date) return 'Please enter effective date.'

      if (this.patternForm.end_time <= this.patternForm.start_time) {
        return 'End time must be later than start time.'
      }

      if (
        this.patternForm.expire_date
        && this.patternForm.expire_date < this.patternForm.effective_date
      ) {
        return 'Expire date cannot be earlier than effective date.'
      }

      return true
    },

    /* =========================================================
       DAILY ROSTER
    ========================================================== */
    getRosterData() {
      if (!this.userId) return

      this.loadingRoster = true

      this.$http
        .get(this.apiRosterList(), {
          params: {
            from: this.rosterFilter.from,
            to: this.rosterFilter.to,
          },
        })
        .then(res => {
          this.rosterRows = res.data.rosters
            || res.data.roster
            || res.data.records
            || res.data.data
            || []
        })
        .catch(error => {
          this.rosterRows = []
          this.showApiError(error, 'Cannot load daily roster.')
        })
        .finally(() => {
          this.loadingRoster = false
        })
    },

    openRosterModal(item = null) {
      if (item) {
        this.rosterForm = {
          id: item.id,
          user_id: this.userId,
          center_id: Number(item.center_id),
          position: item.position || item.position_name || '',
          position_id: item.position_id || null,
          roster_pattern_id: item.roster_pattern_id || null,
          roster_date: item.roster_date,
          start_time: this.datetimeTime(item.start),
          end_time: this.datetimeTime(item.end),
          roster_source: item.roster_source === 'PATTERN'
            ? 'OVERRIDE'
            : (item.roster_source || 'MANUAL'),
          is_leave: item.is_leave || 'N',
          leave_type: item.leave_type || '',
          remarks: item.remarks || '',
          status: item.status || 'ACTIVE',
        }
      } else {
        this.rosterForm = this.emptyRosterForm()
      }

      this.rosterModal = true
    },

    saveRoster() {
      const validation = this.validateRoster()

      if (validation !== true) {
        this.showError(validation)
        return
      }

      this.savingRoster = true

      const payload = {
        user_id: this.userId,
        center_id: Number(this.rosterForm.center_id),

        // NOTE:
        // Your current tbl_roster schema contains position_id INT,
        // while tbl_roster_pattern contains position VARCHAR(255).
        // Backend should map the submitted `position` to your final position design.
        position: this.rosterForm.position || null,
        position_id: this.rosterForm.position_id || null,

        roster_pattern_id: this.rosterForm.roster_pattern_id || null,
        roster_date: this.rosterForm.roster_date,

        start: `${this.rosterForm.roster_date} ${this.normalizeTime(this.rosterForm.start_time)}`,
        end: `${this.rosterForm.roster_date} ${this.normalizeTime(this.rosterForm.end_time)}`,

        roster_source: this.rosterForm.roster_source,
        is_leave: this.rosterForm.is_leave,
        leave_type: this.rosterForm.is_leave === 'Y'
          ? (this.rosterForm.leave_type || null)
          : null,

        remarks: this.rosterForm.remarks || null,
        status: this.rosterForm.status,
      }

      const request = this.rosterForm.id
        ? this.$http.post(`${this.apiRosterItem(this.rosterForm.id)}edit/`, payload)
        : this.$http.post(this.apiRosterList(), payload)

      request
        .then(res => {
          if (res.data.result === false || res.data.success === false) {
            this.showError(res.data.message || 'Save failed.')
            return
          }

          this.rosterModal = false
          this.showSuccess(res.data.message || 'Daily roster saved.')
          this.getRosterData()
        })
        .catch(error => {
          this.showApiError(error, 'Cannot save daily roster.')
        })
        .finally(() => {
          this.savingRoster = false
        })
    },

    confirmDeleteRoster(item) {
      this.$swal({
        title: 'Delete daily roster?',
        text: `${item.roster_date} ${this.datetimeTime(item.start)} - ${this.datetimeTime(item.end)}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteRoster(item)
        }
      })
    },

    deleteRoster(item) {
      this.$http
        .post(`${this.apiRosterItem(item.id)}delete/`, {})
        .then(res => {
          if (res.data.result === false || res.data.success === false) {
            this.showError(res.data.message || 'Delete failed.')
            return
          }

          this.showSuccess(res.data.message || 'Daily roster deleted.')
          this.getRosterData()
        })
        .catch(error => {
          this.showApiError(error, 'Cannot delete daily roster.')
        })
    },

    validateRoster() {
      if (!this.rosterForm.roster_date) return 'Please select roster date.'
      if (!this.rosterForm.center_id) return 'Please select center.'
      if (!this.rosterForm.start_time) return 'Please enter start time.'
      if (!this.rosterForm.end_time) return 'Please enter end time.'

      if (this.rosterForm.end_time <= this.rosterForm.start_time) {
        return 'End time must be later than start time.'
      }

      return true
    },

    syncRosterDateTime() {
      // kept intentionally: start/end datetime is generated when saving
    },

    /* =========================================================
       FORM FACTORIES
    ========================================================== */
    emptyPatternForm() {
      return {
        id: null,
        user_id: this ? this.userId : null,
        center_id: null,
        position: '',
        weekday: null,
        start_time: '09:00',
        end_time: '18:00',
        effective_date: this ? this.today() : '',
        expire_date: '',
        status: 'ACTIVE',
        remarks: '',
      }
    },

    emptyRosterForm() {
      return {
        id: null,
        user_id: this ? this.userId : null,
        center_id: null,
        position: '',
        position_id: null,
        roster_pattern_id: null,
        roster_date: this ? this.today() : '',
        start_time: '09:00',
        end_time: '18:00',
        roster_source: 'MANUAL',
        is_leave: 'N',
        leave_type: '',
        remarks: '',
        status: 'ACTIVE',
      }
    },

    /* =========================================================
       HELPERS
    ========================================================== */
    weekdayName(value) {
      const option = this.weekdayOptions.find(
        item => Number(item.value) === Number(value),
      )

      return option ? option.label : value
    },

    centerName(centerId) {
      const center = this.centerOptions.find(
        item => Number(item.value) === Number(centerId),
      )

      return center ? center.label : `Center ${centerId}`
    },

    shortTime(value) {
      if (!value) return ''
      return String(value).substring(0, 5)
    },

    normalizeTime(value) {
      if (!value) return null

      const text = String(value)

      if (text.length === 5) {
        return `${text}:00`
      }

      return text
    },

    datetimeTime(value) {
      if (!value) return ''

      const text = String(value)
      const match = text.match(/(\d{2}:\d{2})/)

      return match ? match[1] : text
    },

    dateWeekday(dateString) {
      if (!dateString) return ''

      const parts = dateString.split('-')
      const date = new Date(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2]),
      )

      const names = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ]

      return names[date.getDay()]
    },

    today() {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    firstDayOfMonth() {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')

      return `${year}-${month}-01`
    },

    lastDayOfMonth() {
      const now = new Date()
      const date = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    patternStatusVariant(status) {
      if (status === 'ACTIVE') return 'success'
      if (status === 'DISABLED') return 'secondary'
      return 'danger'
    },

    rosterStatusVariant(status) {
      if (status === 'ACTIVE') return 'success'
      if (status === 'CANCELLED') return 'warning'
      return 'danger'
    },

    sourceVariant(source) {
      if (source === 'PATTERN') return 'info'
      if (source === 'OVERRIDE') return 'warning'
      return 'primary'
    },

    showSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-center',
        props: {
          title: 'Success',
          icon: 'CheckIcon',
          variant: 'success',
          text: message,
        },
      })
    },

    showError(message) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-center',
        props: {
          title: 'Error',
          icon: 'XIcon',
          variant: 'danger',
          text: message,
        },
      })
    },

    showApiError(error, fallback) {
      const message = error
        && error.response
        && error.response.data
        && error.response.data.message
        ? error.response.data.message
        : fallback

      this.showError(message)
    },
  },
}
</script>

<style lang="scss">
.roster-shift-card {
  border: 1px solid rgba(34, 41, 47, 0.08);
  border-radius: 6px;
  padding: 0.75rem;
}
</style>
